import React from 'react';
import { Alert, Tag, Typography } from 'antd';
import { CheckCircleFilled } from '@ant-design/icons';
import { Rect, Text } from 'react-konva';
import { useTranslation } from 'react-i18next';
import { TGDDesign, TGraphicDesignLessonConfig } from '../../../models/courses/graphicDesign';
import GDStage from '../canvas/GDStage';
import { evaluate } from '../core/engine';
import { pickI18n } from '../core/i18n';
import GDEditor from '../editor/GDEditor';
import { findRule } from '../admin/ruleTemplates';
import GDMissionPanel from '../student/GDMissionPanel';
import { deriveCritiqueConfig } from '../student/critique';
import { resolveStartDesign } from '../student/session';

interface GraphicDesignLessonTeacherProps {
  config: TGraphicDesignLessonConfig;
  statement?: string;
}

const GOOD = '#0e9f6e';

/**
 * Vue enseignant en lecture seule : l'enonce comme l'eleve, puis la correction
 * adaptee au type d'exercice (solution, zones dessinees sur l'affiche, versions
 * illustrees avec la reponse attendue en clair).
 */
const GraphicDesignLessonTeacher: React.FC<GraphicDesignLessonTeacherProps> = ({
  config, statement }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const text = statement || config.statement;
  const initial = resolveStartDesign(config.initialState, null, config.background);
  const isCritique = config.exerciseType === 'CRITIQUE';
  const evaluationConfig = isCritique ? deriveCritiqueConfig(config) : config;
  const expectedChoice = findRule(config.rules, 'choiceEquals')?.params.choiceId;
  const expectedReason = findRule(config.rules, 'reasonEquals')?.params.reasonId;
  const versions = config.versions ?? [];
  const choices = config.choices ?? [];
  const isRead = !isCritique && versions.length > 0
    && (config.exerciseType === 'CHOOSE' || config.exerciseType === 'INTENT');
  // Pour une intention en lecture (une seule version), le choix attendu est un mot.
  const wordMode = isRead && versions.length <= 1;
  const design = config.solution ?? initial;
  const answers = isCritique
    ? { zones: Object.fromEntries((config.zones ?? []).map(zone => [zone.id, zone.correct])) }
    : { choiceId: expectedChoice, reasonId: expectedReason };
  const evaluation = evaluate(evaluationConfig, { design,
    initial: config.exerciseType === 'TRANSFORM' ? initial : undefined,
    answers, elapsedMs: 0, artboard: config.artboard, direction: i18n.dir(),
    zones: config.zones });
  const panel = <GDMissionPanel config={evaluationConfig} evaluation={evaluation}
    displayedStars={evaluation.stars} hintsUsed={0} attempts={0} teacher />;
  const frame = (item: TGDDesign, maxWidth: number, overlay?: React.ReactNode) =>
    <div className="gd-stage-frame" style={{ maxWidth,
      aspectRatio: `${config.artboard.width} / ${config.artboard.height}` }}>
      <GDStage design={item} artboard={config.artboard} selectedId={null} readOnly
        zoom="fit" direction={i18n.dir()} onSelect={() => undefined}
        onPreview={() => undefined} onCommit={() => undefined}
        backgroundImage={isCritique ? config.backgroundImage : undefined} overlay={overlay} />
    </div>;

  // Zones dessinees et numerotees sur l'affiche : le numero renvoie a la liste.
  const zonesOverlay = (config.zones ?? []).map((zone, index) =>
    <React.Fragment key={zone.id}>
      <Rect x={zone.x} y={zone.y} width={zone.w} height={zone.h} stroke={GOOD}
        strokeWidth={3} listening={false} />
      <Rect x={zone.x} y={zone.y} width={28} height={28} fill={GOOD} listening={false} />
      <Text x={zone.x} y={zone.y + 5} width={28} align="center" text={String(index + 1)}
        fontSize={18} fontStyle="bold" fill="#ffffff" listening={false} />
    </React.Fragment>);

  return <div style={{ display: 'grid', gap: 16 }}>
    {text && <div className="gd-statement">{text}</div>}
    <Alert type="info" showIcon message={t('graphicDesignLesson.teacher.title')}
      description={<>
        <Typography.Paragraph>{pickI18n(config.brief, lang)}</Typography.Paragraph>
        <Typography.Text>{t(`graphicDesignLesson.exerciseTypes.${config.exerciseType}`)}</Typography.Text>
      </>} />

    {isCritique && <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
      <div style={{ flex: '2 1 360px', minWidth: 0 }}>{frame(initial, 720, zonesOverlay)}</div>
      <div className="gd-panel" style={{ flex: '1 1 280px' }}>
        <Typography.Title level={5}>{t('graphicDesignLesson.teacher.zones')}</Typography.Title>
        <ol>{(config.zones ?? []).map(zone => <li key={zone.id} style={{ marginBottom: 8 }}>
          <strong>{pickI18n(zone.options[zone.correct], lang)}</strong>
          {' — '}{pickI18n(zone.lesson, lang)}
        </li>)}</ol>
        {panel}
      </div>
    </div>}

    {isRead && <div style={{ display: 'grid', gap: 16 }}>
      <Typography.Title level={5}>{t('graphicDesignLesson.teacher.versions')}</Typography.Title>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
        {versions.map((version, index) => {
          const expected = !wordMode && version.id === expectedChoice;
          return <div key={version.id} style={{ flex: '1 1 220px', minWidth: 0,
            padding: 8, borderRadius: 12,
            border: `2px solid ${expected ? GOOD : 'transparent'}` }}>
            {frame(version.design, wordMode ? 480 : 300)}
            <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center' }}>
              <strong>{pickI18n(version.label, lang) || String.fromCharCode(65 + index)}</strong>
              {expected && <Tag color="success" icon={<CheckCircleFilled />}>
                {t('graphicDesignLesson.teacher.expected')}</Tag>}
            </div>
          </div>;
        })}
      </div>
      {choices.length > 0 && <section>
        <Typography.Title level={5}>{t(wordMode ? 'graphicDesignLesson.teacher.expectedWord'
          : 'graphicDesignLesson.teacher.expectedReason')}</Typography.Title>
        <ul>{choices.map(choice => {
          const expected = choice.id === (wordMode ? expectedChoice : expectedReason);
          return <li key={choice.id}>
            {expected ? <strong>{pickI18n(choice.label, lang)}</strong>
              : pickI18n(choice.label, lang)}
            {expected && <> {' '}<Tag color="success" icon={<CheckCircleFilled />}>
              {t('graphicDesignLesson.teacher.expected')}</Tag></>}
          </li>;
        })}</ul>
      </section>}
      {panel}
    </div>}

    {!isCritique && !isRead && <GDEditor initialDesign={design} artboard={config.artboard}
      constraints={{ allowedTools: config.allowedTools, lockedPalette: config.lockedPalette,
        maxElements: config.maxElements }} onChange={() => undefined} readOnly
      rightPanel={panel} />}
  </div>;
};

export default GraphicDesignLessonTeacher;
